import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTopupsMutations } from '../../hooks/useTopupsMutations';
import type { Topup } from '../../models';

export interface RejectTopupModalRef {
  open: (topup: Topup) => void;
  close: () => void;
}

interface RejectTopupModalProps {
  onSuccess?: () => void;
}

export const RejectTopupModal = forwardRef<RejectTopupModalRef, RejectTopupModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('topupsModule');
  const { toast } = useToast();
  const currency = useCurrency();
  const modalRef = useRef<ModalCenterRef>(null);
  const { rejectTopup } = useTopupsMutations();

  const [topup, setTopup] = useState<Topup | null>(null);
  const [reason, setReason] = useState('');

  useImperativeHandle(ref, () => ({
    open: (t) => {
      setTopup(t);
      setReason('');
      modalRef.current?.openModal();
    },
    close: () => modalRef.current?.closeModal(),
  }));

  const handleConfirm = async () => {
    if (!topup || !reason.trim()) return;
    try {
      await rejectTopup.mutateAsync({
        id: topup.id,
        payload: { rejection_reason: reason.trim() },
      });
      toast({
        title: t('modals.reject.success.title'),
        description: t('modals.reject.success.description'),
      });
      modalRef.current?.closeModal();
      onSuccess?.();
    } catch (error) {
      toast({
        title: t('errors.rejectFailed'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = rejectTopup.isPending;

  return (
    <ModalCenter ref={modalRef} title={t('modals.reject.title')} width="default">
      <div className="space-y-4">
        <p className="text-sm text-foreground">{t('modals.reject.description')}</p>

        {topup && (
          <div className="bg-muted rounded-lg p-3 space-y-1">
            <p className="text-sm">
              <span className="font-medium">ID:</span> #{topup.id}
            </p>
            <p className="text-sm">
              <span className="font-medium">{t('modals.reject.fields.declared_amount')}:</span> {topup.declared_amount}{' '}
              {currency}
            </p>
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">{t('modals.reject.fields.reason')} *</label>
          <TextArea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={isLoading}
            rows={3}
            placeholder={t('modals.reject.fields.reason_placeholder')}
            maxLength={255}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
            {t('buttons.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={!reason.trim() || isLoading}>
            {isLoading ? t('buttons.rejecting') : t('actions.reject')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

RejectTopupModal.displayName = 'RejectTopupModal';
